import React, { useState, useEffect } from "react"
import {
  Input,
  FormFeedback,
} from "reactstrap"
import logoDark from "../../assets/images/lsda-logo.png"
import { useSelector, useDispatch } from "react-redux"
import { createSelector } from "reselect"
import PropTypes from "prop-types"

import * as Yup from "yup"
import { useFormik } from "formik"
import withRouter from "../../components/Common/withRouter"

import { loginUser } from "../../store/actions"

const selectLoginState = state => state.Login
const makeSelectLoginError = () =>
  createSelector(selectLoginState, login => login.error)

/* ── Module data ── */
const MODULES = [
  { icon: "mdi-domain", name: "Empresas", desc: "Gestión corporativa" },
  { icon: "mdi-file-document-outline", name: "Comprobantes", desc: "Consulta y control" },
  { icon: "mdi-chart-areaspline", name: "Análisis de Ventas", desc: "Inteligencia comercial" },
  { icon: "mdi-credit-card-outline", name: "Medios de Pago", desc: "Análisis financiero" },
  { icon: "mdi-package-variant-closed", name: "Inventarios", desc: "Control de artículos" },
  { icon: "mdi-account-group-outline", name: "Mercadeo", desc: "Perfil de clientes" },
]

const CYCLING_WORDS = [
  "Empresas",
  "Comprobantes",
  "Ventas",
  "Medios de Pago",
  "Inventarios",
  "Clientes",
]

/* ── CSS Keyframes ── */
const keyframes = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

  @keyframes gradientShift {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }
  @keyframes float {
    0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.6; }
    50% { transform: translateY(-20px) rotate(5deg); opacity: 1; }
  }
  @keyframes float2 {
    0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.4; }
    50% { transform: translateY(-15px) rotate(-3deg); opacity: 0.8; }
  }
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }
  @keyframes wordFadeIn {
    0% { opacity: 0; transform: translateY(12px); }
    15% { opacity: 1; transform: translateY(0); }
    85% { opacity: 1; transform: translateY(0); }
    100% { opacity: 0; transform: translateY(-12px); }
  }
  @keyframes moduleGlow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(96,165,250,0); }
    50% { box-shadow: 0 0 20px 0 rgba(96,165,250,0.15); }
  }

  .login-input {
    padding-left: 42px !important;
  }
  .login-input:focus {
    border-color: #3b82f6 !important;
    background: #fff !important;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1), 0 1px 3px rgba(0,0,0,0.08) !important;
  }
  .login-btn:hover {
    transform: translateY(-2px) !important;
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.4) !important;
  }
  .login-btn:active {
    transform: translateY(0) !important;
  }
  .module-card {
    transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1) !important;
  }
  .module-card:hover {
    background: rgba(255,255,255,0.15) !important;
    transform: translateY(-4px) scale(1.02) !important;
    border-color: rgba(96,165,250,0.3) !important;
  }
  .eye-btn:hover {
    color: #3b82f6 !important;
  }

  @media (max-width: 991px) {
    .login-left { display: none !important; }
    .login-right { min-height: 100vh !important; }
  }
`

const Login = props => {
  document.title = "Login | LS-DA S.A.S"

  const dispatch = useDispatch()
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [currentWordIdx, setCurrentWordIdx] = useState(0)

  // Cycling words effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWordIdx(prev => (prev + 1) % CYCLING_WORDS.length)
    }, 2800)
    return () => clearInterval(interval)
  }, [])

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: { email: "", password: "" },
    validationSchema: Yup.object({
      email: Yup.string().required("Ingresa tu email"),
      password: Yup.string().required("Ingresa tu contraseña"),
    }),
    onSubmit: values => {
      setIsLoading(true)
      dispatch(loginUser(values, props.router.navigate))
    },
  })

  const selectLoginError = makeSelectLoginError()
  const error = useSelector(state => selectLoginError(state))

  useEffect(() => {
    if (error) {
      setErrorMessage("Usuario o contraseña incorrecto")
      setShowError(true)
      setIsLoading(false)
    } else {
      setShowError(false)
    }
  }, [error])

  return (
    <>
      <style>{keyframes}</style>
      <div style={{
        minHeight: "100vh",
        display: "flex",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      }}>
        {/* ══════════ LEFT PANEL ══════════ */}
        <div className="login-left" style={{
          flex: "0 0 48%",
          background: "linear-gradient(135deg, #0a0f1e 0%, #0f1b33 25%, #162d50 50%, #1a3a66 75%, #1e4d7a 100%)",
          backgroundSize: "200% 200%",
          animation: "gradientShift 15s ease infinite",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          overflow: "hidden",
          padding: "60px 50px",
        }}>
          {/* Floating decorative orbs */}
          {[
            { w: 300, h: 300, top: -60, right: -80, bg: "rgba(59,130,246,0.08)", anim: "float 8s ease-in-out infinite" },
            { w: 200, h: 200, bottom: 80, left: -40, bg: "rgba(99,102,241,0.06)", anim: "float2 10s ease-in-out infinite 2s" },
            { w: 150, h: 150, top: "40%", right: "5%", bg: "rgba(34,211,238,0.05)", anim: "float 12s ease-in-out infinite 4s" },
          ].map((orb, i) => (
            <div key={i} style={{
              position: "absolute", width: orb.w, height: orb.h,
              borderRadius: "50%", background: orb.bg,
              top: orb.top, right: orb.right, bottom: orb.bottom, left: orb.left,
              animation: orb.anim, filter: "blur(40px)",
            }} />
          ))}

          {/* Grid pattern overlay */}
          <div style={{
            position: "absolute", inset: 0, opacity: 0.03,
            backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)`,
            backgroundSize: "30px 30px",
          }} />

          {/* Content */}
          <div style={{
            position: "relative", zIndex: 1,
            animation: "slideUp 0.8s ease-out",
            width: "100%", maxWidth: 460,
          }}>
            {/* Platform badge */}
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 12,
              padding: "12px 28px",
              borderRadius: 50,
              background: "linear-gradient(135deg, rgba(96,165,250,0.1), rgba(34,211,238,0.08))",
              border: "1px solid rgba(96,165,250,0.25)",
              marginBottom: 32,
              boxShadow: "0 0 20px rgba(96,165,250,0.1), inset 0 0 20px rgba(96,165,250,0.05)",
            }}>
              <div style={{
                width: 10, height: 10, borderRadius: "50%",
                background: "linear-gradient(135deg, #60a5fa, #22d3ee)",
                boxShadow: "0 0 10px rgba(34,211,238,0.6)",
                animation: "blink 2s ease-in-out infinite",
              }} />
              <span style={{
                fontSize: 16, fontWeight: 700,
                letterSpacing: "3px", textTransform: "uppercase",
                background: "linear-gradient(135deg, #e2e8f0, #60a5fa)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}>
                Plataforma LS-DA
              </span>
            </div>

            {/* Animated hero text */}
            <h1 style={{
              fontSize: 38, fontWeight: 800,
              lineHeight: 1.2, letterSpacing: "-1px",
              marginBottom: 12, color: "#fff",
              WebkitTextFillColor: "#fff",
            }}>
              Gestiona tus
              <br />
              <span style={{ position: "relative", display: "inline-block", minWidth: 280 }}>
                <span
                  key={currentWordIdx}
                  style={{
                    display: "inline-block",
                    background: "linear-gradient(135deg, #60a5fa 0%, #38bdf8 50%, #22d3ee 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    animation: "wordFadeIn 2.8s ease-in-out",
                    fontSize: 42,
                  }}
                >
                  {CYCLING_WORDS[currentWordIdx]}
                </span>
                <span style={{
                  display: "inline-block",
                  width: 3, height: 36,
                  background: "#60a5fa",
                  marginLeft: 6,
                  verticalAlign: "middle",
                  animation: "blink 1s step-end infinite",
                  borderRadius: 2,
                }} />
              </span>
              <br />
              <span style={{ color: "#ffffff", WebkitTextFillColor: "#ffffff", fontWeight: 600, fontSize: 30 }}>
                con inteligencia.
              </span>
            </h1>

            <p style={{
              color: "#ffffff", fontSize: 14,
              lineHeight: 1.7, marginBottom: 36,
              maxWidth: 380, opacity: 0.85,
            }}>
              La plataforma que centraliza el control de tus operaciones, análisis de datos y toma de decisiones empresariales.
            </p>

            {/* Module grid */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
            }}>
              {MODULES.map((mod, i) => (
                <div
                  key={i}
                  className="module-card"
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "14px 16px", borderRadius: 14,
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    cursor: "default",
                    animation: `slideUp 0.6s ease-out ${0.1 + i * 0.1}s both`,
                  }}
                >
                  <div style={{
                    width: 38, height: 38, borderRadius: 10,
                    background: `linear-gradient(135deg, rgba(96,165,250,${0.15 + i * 0.03}), rgba(34,211,238,${0.1 + i * 0.02}))`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    <i className={`mdi ${mod.icon}`} style={{ fontSize: 18, color: "#60a5fa" }} />
                  </div>
                  <div>
                    <div style={{ color: "#e2e8f0", fontSize: 13, fontWeight: 600, lineHeight: 1.2 }}>{mod.name}</div>
                    <div style={{ color: "rgba(255,255,255,0.35)", fontSize: 11 }}>{mod.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom copyright */}
          <div style={{
            position: "absolute", bottom: 28,
            color: "rgba(255,255,255,0.6)", fontSize: 12,
            letterSpacing: "0.3px",
          }}>
            © {new Date().getFullYear()} LS-DA S.A.S
          </div>
        </div>

        {/* ══════════ RIGHT PANEL ══════════ */}
        <div className="login-right" style={{
          flex: 1, display: "flex", flexDirection: "column",
          justifyContent: "center", alignItems: "center",
          padding: "40px 20px",
          background: "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)",
          position: "relative",
        }}>
          {/* Subtle pattern */}
          <div style={{
            position: "absolute", inset: 0, opacity: 0.4,
            backgroundImage: "radial-gradient(circle at 1px 1px, #e2e8f0 1px, transparent 0)",
            backgroundSize: "40px 40px",
          }} />

          <div style={{
            width: "100%", maxWidth: 420, position: "relative", zIndex: 1,
            animation: "slideUp 0.6s ease-out",
          }}>
            {/* Logo */}
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <img src={logoDark} alt="LS-DA" style={{ height: 110 }} />
            </div>

            {/* Form card */}
            <div style={{
              background: "#fff",
              borderRadius: 24,
              padding: "44px 40px 36px",
              boxShadow: "0 4px 6px rgba(0,0,0,0.04), 0 10px 40px rgba(0,0,0,0.06)",
              border: "1px solid rgba(226,232,240,0.8)",
            }}>
              {/* Header */}
              <div style={{ marginBottom: 32 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 14,
                  background: "linear-gradient(135deg, #0f172a, #1e3a5f)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  marginBottom: 20,
                  boxShadow: "0 4px 12px rgba(15, 23, 42, 0.2)",
                }}>
                  <i className="mdi mdi-lock-outline" style={{ fontSize: 22, color: "#fff" }} />
                </div>
                <h3 style={{
                  fontSize: 24, fontWeight: 800, color: "#0f172a",
                  marginBottom: 6, letterSpacing: "-0.5px",
                }}>
                  Iniciar Sesión
                </h3>
                <p style={{
                  color: "#94a3b8", fontSize: 14, margin: 0, lineHeight: 1.5,
                }}>
                  Ingresa tus credenciales para acceder al portal
                </p>
              </div>

              {/* Form */}
              <form onSubmit={e => {
                e.preventDefault()
                setShowError(false)
                validation.handleSubmit()
              }}>
                {/* Error alert */}
                {showError && errorMessage && (
                  <div style={{
                    borderRadius: 12, fontSize: 13, padding: "12px 16px",
                    background: "linear-gradient(135deg, #fef2f2, #fff1f2)",
                    color: "#dc2626",
                    display: "flex", alignItems: "center", gap: 10,
                    marginBottom: 24,
                    border: "1px solid #fecaca",
                    animation: "slideUp 0.3s ease-out",
                  }}>
                    <i className="mdi mdi-alert-circle" style={{ fontSize: 20, flexShrink: 0 }} />
                    <span style={{ fontWeight: 500 }}>{errorMessage}</span>
                  </div>
                )}

                {/* Email field */}
                <div style={{ marginBottom: 20 }}>
                  <label style={{
                    display: "block", fontSize: 13, fontWeight: 600,
                    color: "#334155", marginBottom: 8,
                  }}>
                    Correo electrónico
                  </label>
                  <div style={{ position: "relative" }}>
                    <i className="mdi mdi-email-outline" style={{
                      position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
                      color: "#94a3b8", fontSize: 18, pointerEvents: "none", zIndex: 2,
                    }} />
                    <Input
                      name="email"
                      type="email"
                      className="login-input"
                      placeholder="tu@empresa.com"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.email || ""}
                      invalid={validation.touched.email && validation.errors.email ? true : false}
                      style={{
                        borderRadius: 12, border: "1.5px solid #e2e8f0",
                        padding: "13px 16px 13px 42px", fontSize: 14,
                        transition: "all 0.25s ease",
                        background: "#f8fafc",
                        fontFamily: "inherit",
                      }}
                    />
                    {validation.touched.email && validation.errors.email && (
                      <FormFeedback type="invalid">{validation.errors.email}</FormFeedback>
                    )}
                  </div>
                </div>

                {/* Password field */}
                <div style={{ marginBottom: 28 }}>
                  <label style={{
                    display: "block", fontSize: 13, fontWeight: 600,
                    color: "#334155", marginBottom: 8,
                  }}>
                    Contraseña
                  </label>
                  <div style={{ position: "relative" }}>
                    <i className="mdi mdi-lock-outline" style={{
                      position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
                      color: "#94a3b8", fontSize: 18, pointerEvents: "none", zIndex: 2,
                    }} />
                    <Input
                      name="password"
                      type={showPassword ? "text" : "password"}
                      className="login-input"
                      placeholder="••••••••"
                      value={validation.values.password || ""}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={validation.touched.password && validation.errors.password ? true : false}
                      style={{
                        borderRadius: 12, border: "1.5px solid #e2e8f0",
                        padding: "13px 48px 13px 42px", fontSize: 14,
                        transition: "all 0.25s ease",
                        background: "#f8fafc",
                        fontFamily: "inherit",
                      }}
                    />
                    <button
                      type="button"
                      className="eye-btn"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)",
                        background: "none", border: "none", cursor: "pointer",
                        color: "#94a3b8", fontSize: 18, padding: 4,
                        transition: "color 0.2s", lineHeight: 1, zIndex: 2,
                      }}
                      tabIndex={-1}
                    >
                      <i className={`mdi ${showPassword ? "mdi-eye-off-outline" : "mdi-eye-outline"}`} />
                    </button>
                    {validation.touched.password && validation.errors.password && (
                      <FormFeedback type="invalid">{validation.errors.password}</FormFeedback>
                    )}
                  </div>
                </div>

                {/* Submit button */}
                <button
                  type="submit"
                  className="login-btn"
                  disabled={isLoading}
                  style={{
                    width: "100%", padding: "14px 24px",
                    background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #1a4f80 100%)",
                    color: "#fff", border: "none", borderRadius: 12,
                    fontSize: 15, fontWeight: 600, cursor: isLoading ? "wait" : "pointer",
                    transition: "all 0.3s ease",
                    boxShadow: "0 6px 20px rgba(15, 23, 42, 0.3)",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                    fontFamily: "inherit", letterSpacing: "0.2px",
                    opacity: isLoading ? 0.85 : 1,
                  }}
                >
                  {isLoading ? (
                    <>
                      <div style={{
                        width: 18, height: 18,
                        border: "2.5px solid rgba(255,255,255,0.25)",
                        borderTop: "2.5px solid #fff",
                        borderRadius: "50%",
                        animation: "spin 0.7s linear infinite",
                      }} />
                      Ingresando...
                    </>
                  ) : (
                    <>
                      Iniciar Sesión
                      <i className="mdi mdi-arrow-right" style={{ fontSize: 18 }} />
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Footer */}
            <div style={{
              textAlign: "center", marginTop: 24,
              color: "#94a3b8", fontSize: 12,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              <i className="mdi mdi-shield-check" style={{ fontSize: 14 }} />
              Conexión segura · LS-DA S.A.S
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default withRouter(Login)

Login.propTypes = {
  history: PropTypes.object,
}
